/**
 * Status Bar Component
 *
 * Shows location information, recording controls, and compass when location permission is granted
 */

import { LitElement, html, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { convertElevation, convertVelocity, type UnitSystem } from '../../utils/units.js'
import type { ActivityType } from 'cairn-compass'

@customElement('status-bar')
export class StatusBar extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      height: 100%;
      padding: 0 16px;
      box-sizing: border-box;
      font-size: 13px;
      font-weight: 500;
      color: white;
    }

    .location-info {
      display: flex;
      gap: 16px;
      align-items: flex-start;
    }

    .info-item {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .info-label {
      font-size: 10px;
      opacity: 0.7;
      line-height: 1;
    }

    .info-value {
      font-size: 13px;
      font-weight: 500;
      line-height: 1;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .record-button {
      background: #ef4444;
      border: none;
      border-radius: 50%;
      width: 36px;
      height: 36px;
      color: white;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
    }

    .record-button:hover {
      background: #dc2626;
      transform: scale(1.05);
    }

    .record-button.recording {
      background: #dc2626;
      animation: pulse 2s infinite;
    }

    .record-button .material-icons {
      color: white;
    }

    .record-button.recording .material-icons {
      /* Make stop icon appear as a filled square */
      font-variation-settings: 'FILL' 1;
    }

    @keyframes pulse {
      0% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.1);
      }
      100% {
        transform: scale(1);
      }
    }

    .material-icons {
      font-family: 'Material Symbols Outlined';
      font-size: 18px;
    }

    .activity-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0.85;
    }

    .activity-icon .material-icons {
      font-size: 20px;
    }

    .determining-dots {
      display: inline-flex;
      gap: 2px;
      align-items: center;
      height: 18px;
    }

    .determining-dots span {
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: white;
      animation: dot-fade 1.4s infinite ease-in-out;
    }

    .determining-dots span:nth-child(2) {
      animation-delay: 0.2s;
    }

    .determining-dots span:nth-child(3) {
      animation-delay: 0.4s;
    }

    @keyframes dot-fade {
      0%,
      80%,
      100% {
        opacity: 0.15;
      }
      40% {
        opacity: 1;
      }
    }
  `

  @property({ type: Object })
  currentLocation?: { lat: number; lng: number; accuracy?: number }

  @property({ type: Number })
  currentElevation?: number

  @property({ type: Number })
  currentHeading?: number

  @property({ type: Number })
  currentVelocity?: number

  @property({ type: String })
  unitSystem: UnitSystem = 'imperial'

  @property({ type: Boolean })
  isRecording = false

  @property({ type: Boolean })
  locationPermissionGranted = false

  @property({ type: String })
  currentActivity?: ActivityType

  private renderActivityValue(): unknown {
    const activity = this.currentActivity
    if (!activity || activity === 'determining' || activity === 'unknown') {
      return html`<div class="determining-dots"><span></span><span></span><span></span></div>`
    }
    const icon = this.activityIcon(activity)
    return html`<span class="material-icons" style="font-size:18px">${icon}</span>`
  }

  private activityIcon(activity: ActivityType): string {
    switch (activity) {
      case 'stationary':
        return 'man'
      case 'walking':
        return 'directions_walk'
      case 'running':
        return 'directions_run'
      case 'cycling':
        return 'directions_bike'
      case 'vehicle':
        return 'directions_car'
      default:
        return 'man'
    }
  }

  private toggleRecording(): void {
    this.dispatchEvent(
      new CustomEvent('toggle-recording', {
        detail: { isRecording: !this.isRecording },
        bubbles: true,
      })
    )
  }

  override render(): unknown {
    if (!this.locationPermissionGranted) {
      return html``
    }

    const elevation =
      this.currentElevation !== undefined
        ? convertElevation(this.currentElevation, this.unitSystem)
        : null
    const velocity =
      this.currentVelocity !== undefined
        ? convertVelocity(this.currentVelocity, this.unitSystem)
        : null

    return html`
      <div class="location-info">
        <div class="info-item">
          <div class="info-label">ACTIVITY</div>
          <div class="info-value">${this.renderActivityValue()}</div>
        </div>
        <div class="info-item">
          <div class="info-label">ELEVATION</div>
          <div class="info-value">${elevation ?? '--'}</div>
        </div>
        <div class="info-item">
          <div class="info-label">SPEED</div>
          <div class="info-value">${velocity ?? '--'}</div>
        </div>
        ${this.currentHeading !== undefined
          ? html`
              <div class="info-item">
                <div class="info-label">ORIENTATION</div>
                <div class="info-value">${Math.round(this.currentHeading)}°</div>
              </div>
            `
          : ''}
      </div>

      <button
        class="record-button ${this.isRecording ? 'recording' : ''}"
        @click=${this.toggleRecording}
        title="${this.isRecording ? 'Stop recording' : 'Start recording'}"
      >
        <span class="material-icons"> ${this.isRecording ? 'square' : 'circle'} </span>
      </button>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'status-bar': StatusBar
  }
}
